/**
 * Quest guidance by objective TYPE (AD-867, story `q-quest-progress-signals`). Owner, 2026-10-25:
 * stations and NPCs are explicit on the map; "a resource or enemy etc. to find should be a rough
 * location or radius only so the player is guided but still has to explore a bit"; and the
 * signals must apply to "all future quests". So the marker kind is decided by the verb, never by
 * a quest or a content row, and the table is typed `Record<QuestVerb, …>`: a new verb does not
 * compile until it says how the player is guided to it.
 *
 * Pure and shared: the client draws from it, the backend computes enemy areas with it, and the
 * content tests check every shipped quest against it.
 */
import { QUEST_VERBS } from '../content/types.js';
/** How the HUD guides the player to each verb's target (AD-867). */
export const OBJECTIVE_MARKER = {
    gather: 'area', // find the resource: the spawn cluster, never the node
    kill: 'area', // find the creature: its spawn area, never the creature
    craft: 'pin', // a station is a fixed place
    talk: 'pin', // an NPC stands where they stand
    deliver: 'pin', // the recipient NPC
    visit: 'pin', // the zone exit that leads there
};
/** The marker kind for a verb string off the wire; an unknown verb guides by area (weakest). */
export function markerKindFor(verb) {
    return QUEST_VERBS.includes(verb)
        ? OBJECTIVE_MARKER[verb]
        : 'area';
}
/** Points closer than this belong to the same search area. */
export const AREA_LINK_M = 30;
/** Every area reaches this far past its outermost point (the player still has to look). */
export const AREA_PAD_M = 6;
/** No area is smaller than this, so even a single node is a place to search, not a dot. */
export const AREA_MIN_RADIUS_M = 12;
/**
 * Group points into search areas (single-link clusters at {@link AREA_LINK_M}) and describe each
 * as a circle around its centroid that covers every member with {@link AREA_PAD_M} to spare,
 * never smaller than {@link AREA_MIN_RADIUS_M}. Deterministic for a given point order; areas are
 * returned largest-membership first.
 */
export function searchAreas(points) {
    const n = points.length;
    const group = new Array(n).fill(-1);
    let groups = 0;
    for (let i = 0; i < n; i += 1) {
        if (group[i] !== -1)
            continue;
        group[i] = groups;
        const stack = [i];
        while (stack.length > 0) {
            const a = points[stack.pop()];
            for (let j = 0; j < n; j += 1) {
                if (group[j] !== -1)
                    continue;
                const b = points[j];
                if (Math.hypot(a.x - b.x, a.z - b.z) <= AREA_LINK_M) {
                    group[j] = groups;
                    stack.push(j);
                }
            }
        }
        groups += 1;
    }
    const out = [];
    for (let g = 0; g < groups; g += 1) {
        const members = points.filter((_, i) => group[i] === g);
        const cx = members.reduce((s, p) => s + p.x, 0) / members.length;
        const cz = members.reduce((s, p) => s + p.z, 0) / members.length;
        const far = members.reduce((m, p) => Math.max(m, Math.hypot(p.x - cx, p.z - cz)), 0);
        out.push({
            area: {
                x: Math.round(cx * 10) / 10,
                z: Math.round(cz * 10) / 10,
                radiusM: Math.max(AREA_MIN_RADIUS_M, Math.ceil(far + AREA_PAD_M)),
            },
            members: members.length,
        });
    }
    out.sort((a, b) => b.members - a.members);
    return out.map((o) => o.area);
}
/** The search area nearest to `from` (by distance to its edge), or null when there is none. */
export function nearestArea(from, areas) {
    if (areas.length === 0)
        return null;
    if (from === null)
        return areas[0] ?? null;
    let best = null;
    let bestD = Infinity;
    for (const a of areas) {
        const d = Math.max(0, Math.hypot(a.x - from.x, a.z - from.z) - a.radiusM);
        if (d < bestD) {
            bestD = d;
            best = a;
        }
    }
    return best;
}
/**
 * Is this quest ready to hand in at its turn-in NPC (AD-867; q-quest-marker-after-craft)? True when
 * it is `complete`, AND when it is `active` but every objective still open is a `talk` to that same
 * NPC: talking there meets them and the same conversation offers the turn-in (the server applies
 * the talk before it builds the dialogue), so the player's next and only step is that NPC. Objective
 * order is not enforced server-side (`applyFact`), so neither is it here. A `deliver` objective is
 * NOT counted: it needs the item in the bag, which the quest log does not carry.
 */
export function isReadyToTurnIn(q) {
    if (!q.turnInNpc)
        return false;
    if (q.status === 'complete')
        return true;
    if (q.status !== 'active')
        return false;
    const open = (q.objectives ?? []).filter((o) => !o.done);
    return open.length > 0 && open.every((o) => o.verb === 'talk' && o.target === q.turnInNpc);
}
/**
 * The NPCs the player's quest log is ready to turn in at (AD-867): every quest
 * {@link isReadyToTurnIn} names its turn-in NPC. Generic over quests: the "!" marker, the map pin
 * and the HUD's "Return to …" read status and objectives, never a quest id.
 */
export function readyTurnInNpcs(quests) {
    const out = new Set();
    for (const q of quests ?? [])
        if (isReadyToTurnIn(q) && q.turnInNpc)
            out.add(q.turnInNpc);
    return out;
}
/**
 * The NPCs with something for the player now, from the quest log: the server's quest offers
 * (the same availability test a talk's offers use) plus the NPCs with lore topics. `null` when
 * the log carries no `offerNpcs` (no log yet, or an older server, AD-125): the caller then
 * falls back to the questgiver role.
 */
export function offerNpcSet(log) {
    if (!log || log.offerNpcs === undefined)
        return null;
    return new Set([...log.offerNpcs, ...(log.infoNpcs ?? [])]);
}
/**
 * The marker an NPC wears. A turn-in outranks everything; a vendor always has goods for you;
 * otherwise the NPC needs an offer or lore (`offer`, from `offerNpcSet`). With no offer set
 * (`null`) a questgiver still wears the "?", as before the server sent one. Nothing for you ⇒ null.
 */
export function npcMarkerKind(npcId, roles, turnIn, offer) {
    if (turnIn.has(npcId))
        return 'turnIn';
    if (roles.includes('vendor'))
        return 'offer';
    if (offer === null)
        return roles.includes('questgiver') ? 'offer' : null;
    return offer.has(npcId) ? 'offer' : null;
}
//# sourceMappingURL=guidance.js.map